// src/pages/ContactPage.js
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Mail, Phone } from 'lucide-react';
import { pageVariants, pageTransition } from '../utils/pageTransitions';
import { trackContactFormSubmit, getStoredUTM } from '../utils/analytics';

const ContactPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = (e) => {
    setIsSubmitting(true);

    // Track conversion with UTM attribution
    trackContactFormSubmit();

    // FormSubmit will handle the actual submission
    // The loading state will be cleared when the page redirects
  };

  return (
    <motion.div
      initial="initial"
      animate="in"
      exit="out"
      variants={pageVariants}
      transition={pageTransition}
      className="min-h-screen pt-20"
    >
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50 min-h-[calc(100vh-80px)]">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-center mb-16"
            >
              <h1 className="text-4xl font-bold mb-4 text-gray-900">Let's Transform Your Resident Services</h1>
              <p className="text-xl text-gray-600">
                Get started in just 3 days with our full-service onboarding
              </p>
            </motion.div>

            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-12"
            >
              <div>
                <h3 className="text-2xl font-bold mb-6 text-gray-900">Get in Touch</h3>
                <form
                  action="https://formsubmit.co/willem@gethousingai.com"
                  method="POST"
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  {/* Hidden fields for FormSubmit configuration */}
                  <input type="hidden" name="_subject" value="New Contact from HousingAI Website" />
                  <input type="hidden" name="_cc" value="easeen@gethousingai.com" />
                  <input type="hidden" name="_next" value={`${window.location.origin}/contact?success=true`} />
                  <input type="hidden" name="_captcha" value="true" />
                  <input type="hidden" name="_template" value="table" />
                  <input type="hidden" name="_replyto" value="email" />

                  {/* Honeypot field for spam protection */}
                  <input type="text" name="_honey" style={{ display: 'none' }} />

                  {/* Pass UTM attribution to FormSubmit email */}
                  <input type="hidden" name="Traffic Source" value={(() => {
                    try {
                      const utm = JSON.parse(sessionStorage.getItem('hai_utm') || '{}');
                      return Object.entries(utm).map(([k, v]) => `${k}=${v}`).join(', ') || 'direct';
                    } catch { return 'direct'; }
                  })()} />

                  <input
                    type="text"
                    name="name"
                    placeholder="Your Name"
                    required
                    className="w-full px-4 py-3 rounded-lg bg-white border border-gray-300 placeholder-gray-400 text-gray-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    required
                    className="w-full px-4 py-3 rounded-lg bg-white border border-gray-300 placeholder-gray-400 text-gray-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                  />
                  <textarea
                    name="message"
                    placeholder="Tell us about your needs..."
                    rows={4}
                    required
                    className="w-full px-4 py-3 rounded-lg bg-white border border-gray-300 placeholder-gray-400 text-gray-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all resize-none"
                  />
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-blue-600 text-white py-4 rounded-full font-semibold hover:bg-blue-700 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>
                </form>

                {/* Success message - only shows if URL has success parameter */}
                {window.location.search.includes('success=true') && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg text-center"
                  >
                    <p className="text-green-700">Thank you! Your message has been sent successfully.</p>
                  </motion.div>
                )}
              </div>

              <div>
                <h3 className="text-2xl font-bold mb-6 text-gray-900">Quick Connect</h3>
                <div className="space-y-6">
                  <motion.div
                    initial={{ x: 20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    className="bg-white rounded-2xl p-6 shadow-lg"
                  >
                    <h4 className="font-semibold mb-2 flex items-center gap-2 text-gray-900">
                      <Mail className="h-5 w-5 text-blue-600" /> Email Us
                    </h4>
                    <a 
                      href="mailto:contact@gethousingai.com" 
                      className="text-blue-600 hover:text-blue-700 underline transition-colors"
                    >
                      contact@gethousingai.com
                    </a>
                  </motion.div>
                </div>

                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.7 }}
                  className="mt-8"
                >
                  <h4 className="font-semibold mb-3 text-gray-900">Our Mission</h4>
                  <p className="text-gray-600 leading-relaxed">
                    We empower Public Housing Authorities to advance fair housing by providing
                    accessible, inclusive, and responsive chatbot technology that enhances
                    resident support and reduces systemic barriers.
                  </p>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default ContactPage;

// ===== END OF src/pages/ContactPage.js =====